import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from './spinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * `primary` — the one action this screen is for.
   * `outline` — an alternative way to do the same thing (a passkey, a
   *   provider). Full width and the same height as `primary`, so a column of
   *   them reads as one list of equals rather than a ranking.
   * `link` — a tertiary escape hatch. Centred by default: left-aligned text
   *   under centred buttons looks like a mistake, and did.
   */
  variant?: 'primary' | 'outline' | 'link';
  loading?: boolean;
  /**
   * A brand mark, pinned to the left edge while the label stays optically
   * centred — so a column of provider buttons has its icons in one clean
   * column and its labels on one axis, which centring the icon+label pair
   * together cannot do once the labels differ in width.
   *
   * Decorative by definition: the label already names the provider, so this is
   * `aria-hidden` and never the only thing carrying the meaning.
   */
  icon?: ReactNode;
  children: ReactNode;
}

const VARIANTS = {
  primary: [
    'min-h-12 w-full rounded-full',
    'bg-buds-dark text-buds-cream hover:bg-buds-dark/90',
    // /40 on cream reads as broken rather than not-yet; this stays legible
    // while still clearly inactive.
    'disabled:bg-buds-dark/55 disabled:text-buds-cream/80',
  ].join(' '),
  outline: [
    'min-h-12 w-full rounded-full',
    'border border-buds-dark/15 bg-buds-surface text-buds-dark',
    'hover:border-buds-dark/30 hover:bg-buds-dark/[0.03]',
    'disabled:opacity-55',
  ].join(' '),
  link: [
    // min-h-11 = the 44px target components.md demands; the text stays small,
    // the tappable box does not.
    'min-h-11 w-full py-1 text-buds-dark underline underline-offset-4',
    'hover:text-buds-dark/70 disabled:text-buds-muted',
  ].join(' '),
} as const;

/**
 * Horizontal padding lives here rather than in VARIANTS: two `px-*` classes on
 * one element are resolved by stylesheet order, not by the order they are
 * written, so a variant's `px-6` could silently win over the wider padding an
 * icon needs. One source, no collision.
 */
const PADDING = {
  primary: 'px-6',
  outline: 'px-6',
  link: '',
} as const;

/** Wide enough that a centred label cannot reach the mark pinned at left-5. */
const PADDING_WITH_ICON = 'px-14';

export function Button({
  variant = 'primary',
  loading = false,
  disabled,
  icon,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        'relative inline-flex items-center justify-center gap-2 text-sm font-semibold transition',
        'focus-ring',
        'motion-reduce:transition-none disabled:cursor-not-allowed',
        VARIANTS[variant],
        icon ? PADDING_WITH_ICON : PADDING[variant],
        className,
      ].join(' ')}
    >
      {icon ? (
        // Full-height rather than a fixed box: Apple's mark is a 12x44
        // button-height lockup whose padding may not be cropped, so it sizes
        // itself off the button (see provider-marks). The others set their own
        // pixel size and centre inside this.
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-5 flex items-center justify-center"
        >
          {icon}
        </span>
      ) : null}
      {loading ? <Spinner /> : null}
      {children}
    </button>
  );
}
